// src/app/pages/auth/KycVerificationPage.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Camera, UploadCloud, RefreshCw, CheckCircle2, XCircle } from "lucide-react";
import { submitKyc } from "../../features/auth/kycApi.js";
import { useAuthSession } from "../../features/auth/useAuth.js";

const MAX_MB = 8;

function bytesToMB(n) {
  return (n / (1024 * 1024)).toFixed(2);
}

function fileTooLarge(file) {
  if (!file) return false;
  return file.size > MAX_MB * 1024 * 1024;
}

function toFileFromCanvas(canvas, filename = "selfie.jpg", mime = "image/jpeg", quality = 0.92) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) return reject(new Error("Failed to capture image"));
        const f = new File([blob], filename, { type: mime });
        resolve(f);
      },
      mime,
      quality
    );
  });
}

export default function KycVerificationPage() {
  const nav = useNavigate();
  const location = useLocation();
  const { user } = useAuthSession();

  // ID document file
  const [idDoc, setIdDoc] = useState(null);

  // selfie: either uploaded file or captured file
  const [selfieFile, setSelfieFile] = useState(null);
  const [selfieCaptured, setSelfieCaptured] = useState(null);

  // UI state
  const [mode, setMode] = useState("upload"); // upload | camera
  const [err, setErr] = useState("");
  const [okMsg, setOkMsg] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // camera refs
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  const selfiePreviewUrl = useMemo(() => {
    const f = selfieCaptured || selfieFile;
    if (!f) return "";
    return URL.createObjectURL(f);
  }, [selfieCaptured, selfieFile]);

  useEffect(() => {
    return () => {
      // cleanup object URL
      if (selfiePreviewUrl) URL.revokeObjectURL(selfiePreviewUrl);
    };
  }, [selfiePreviewUrl]);

  async function stopCamera() {
    try {
      const s = streamRef.current;
      if (s) {
        s.getTracks()?.forEach((t) => t.stop());
      }
      streamRef.current = null;
      if (videoRef.current) videoRef.current.srcObject = null;
    } catch {
      // ignore
    }
  }

  async function startCamera() {
    setErr("");
    setOkMsg("");
    await stopCamera();

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user" },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
    } catch (e) {
      setErr(e?.message || "Failed to access camera. Please allow camera permissions.");
      await stopCamera();
    }
  }

  useEffect(() => {
    if (mode === "camera") startCamera();
    else stopCamera();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  async function captureSelfie() {
    setErr("");
    setOkMsg("");

    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) {
      setErr("Camera not ready");
      return;
    }

    const w = video.videoWidth || 640;
    const h = video.videoHeight || 480;

    canvas.width = w;
    canvas.height = h;

    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, w, h);

    try {
      const file = await toFileFromCanvas(canvas, `selfie_${Date.now()}.jpg`, "image/jpeg");
      if (fileTooLarge(file)) {
        setErr(`Captured image too large (${bytesToMB(file.size)} MB). Please try again.`);
        return;
      }
      setSelfieCaptured(file);
      setSelfieFile(null); // ensure only one source is used
      setOkMsg("Selfie captured. You can submit now.");
    } catch (e) {
      setErr(e?.message || "Failed to capture selfie.");
    }
  }

  function onPickSelfieUpload(file) {
    setErr("");
    setOkMsg("");
    if (!file) return;

    if (fileTooLarge(file)) {
      setErr(`Selfie file too large (${bytesToMB(file.size)} MB). Max ${MAX_MB}MB.`);
      return;
    }

    setSelfieFile(file);
    setSelfieCaptured(null);
  }

  function onPickIdDoc(file) {
    setErr("");
    setOkMsg("");
    if (!file) return;

    if (fileTooLarge(file)) {
      setErr(`ID document too large (${bytesToMB(file.size)} MB). Max ${MAX_MB}MB.`);
      return;
    }

    setIdDoc(file);
  }

  async function onSubmit() {
    setErr("");
    setOkMsg("");

    if (!idDoc) {
      setErr("Please upload your ID document (image or PDF).");
      return;
    }

    const selfie = selfieCaptured || selfieFile;
    if (!selfie) {
      setErr("Please provide a selfie (upload or use the camera).");
      return;
    }

    const form = new FormData();
    form.append("id_document", idDoc);

    // Keep backend contract: accept either selfie_file or selfie_capture
    if (selfieCaptured) form.append("selfie_capture", selfieCaptured);
    else form.append("selfie_file", selfieFile);

    setSubmitting(true);
    try {
      const resp = await submitKyc(form);
      setOkMsg(resp?.message || "KYC submitted successfully.");

      // Optionally redirect to pending
      nav("/auth/kyc-pending", { replace: true, state: { from: location } });
    } catch (e) {
      setErr(e?.message || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="w-full max-w-2xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-900 mb-2">KYC Verification</h1>
      <p className="text-sm text-gray-600 mb-6">
        Upload an ID document and provide a selfie. You can either upload a selfie image or take one using your camera.
      </p>

      {err ? (
        <div className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-start gap-2">
          <XCircle size={18} className="mt-0.5" />
          <div>{err}</div>
        </div>
      ) : null}

      {okMsg ? (
        <div className="mb-4 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 flex items-start gap-2">
          <CheckCircle2 size={18} className="mt-0.5" />
          <div>{okMsg}</div>
        </div>
      ) : null}

      {/* ID Document */}
      <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm mb-6">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="font-semibold text-gray-900">ID Document</div>
            <div className="text-xs text-gray-500">Accepted: JPG, PNG, WEBP, PDF (max {MAX_MB}MB)</div>
          </div>

          <label className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-gray-900 text-white text-sm cursor-pointer">
            <UploadCloud size={16} />
            Upload
            <input
              type="file"
              accept="image/*,application/pdf"
              className="hidden"
              onChange={(e) => onPickIdDoc(e.target.files?.[0] || null)}
            />
          </label>
        </div>

        <div className="mt-3 text-sm text-gray-700">
          {idDoc ? (
            <div className="flex items-center justify-between gap-3 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
              <div className="truncate">
                <div className="font-medium">{idDoc.name}</div>
                <div className="text-xs text-gray-500">
                  {idDoc.type || "unknown"} • {bytesToMB(idDoc.size)} MB
                </div>
              </div>
              <button
                type="button"
                className="text-xs px-3 py-2 rounded-lg bg-white border border-gray-200 hover:bg-gray-100"
                onClick={() => setIdDoc(null)}
              >
                Remove
              </button>
            </div>
          ) : (
            <div className="text-gray-500">No document selected.</div>
          )}
        </div>
      </div>

      {/* Selfie */}
      <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div>
            <div className="font-semibold text-gray-900">Selfie</div>
            <div className="text-xs text-gray-500">Provide a selfie by uploading or using your camera.</div>
          </div>

          <div className="inline-flex rounded-xl border border-gray-200 overflow-hidden">
            <button
              type="button"
              onClick={() => setMode("upload")}
              className={`px-3 py-2 text-sm ${mode === "upload" ? "bg-gray-900 text-white" : "bg-white text-gray-700 hover:bg-gray-50"}`}
            >
              Upload
            </button>
            <button
              type="button"
              onClick={() => setMode("camera")}
              className={`px-3 py-2 text-sm ${mode === "camera" ? "bg-gray-900 text-white" : "bg-white text-gray-700 hover:bg-gray-50"}`}
            >
              Camera
            </button>
          </div>
        </div>

        {mode === "upload" ? (
          <div className="space-y-3">
            <label className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-gray-900 text-white text-sm cursor-pointer">
              <UploadCloud size={16} />
              Upload Selfie
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => onPickSelfieUpload(e.target.files?.[0] || null)}
              />
            </label>

            {selfiePreviewUrl ? (
              <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
                <div className="text-xs text-gray-500 mb-2">Preview</div>
                <img
                  src={selfiePreviewUrl}
                  alt="Selfie preview"
                  className="w-full max-h-[360px] object-contain rounded-xl border border-gray-200 bg-white"
                />
                <div className="mt-3 flex items-center justify-between">
                  <div className="text-xs text-gray-600">
                    {(selfieCaptured || selfieFile)?.name} • {bytesToMB((selfieCaptured || selfieFile)?.size || 0)} MB
                  </div>
                  <button
                    type="button"
                    className="text-xs px-3 py-2 rounded-lg bg-white border border-gray-200 hover:bg-gray-100"
                    onClick={() => {
                      setSelfieFile(null);
                      setSelfieCaptured(null);
                    }}
                  >
                    Remove
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-sm text-gray-500">No selfie selected.</div>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="text-sm font-medium text-gray-900">Camera Preview</div>
                <button
                  type="button"
                  className="inline-flex items-center gap-2 text-xs px-3 py-2 rounded-lg bg-white border border-gray-200 hover:bg-gray-100"
                  onClick={startCamera}
                >
                  <RefreshCw size={14} />
                  Restart camera
                </button>
              </div>

              <video
                ref={videoRef}
                playsInline
                muted
                className="w-full rounded-xl border border-gray-200 bg-black"
              />

              <canvas ref={canvasRef} className="hidden" />

              <div className="mt-3 flex items-center gap-2">
                <button
                  type="button"
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-gray-900 text-white text-sm hover:bg-gray-800"
                  onClick={captureSelfie}
                >
                  <Camera size={16} />
                  Capture
                </button>

                <button
                  type="button"
                  className="text-sm px-4 py-2 rounded-xl bg-white border border-gray-200 hover:bg-gray-100"
                  onClick={() => {
                    setSelfieCaptured(null);
                    setOkMsg("");
                    setErr("");
                  }}
                >
                  Clear
                </button>
              </div>
            </div>

            {selfiePreviewUrl ? (
              <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
                <div className="text-xs text-gray-500 mb-2">Captured Selfie Preview</div>
                <img
                  src={selfiePreviewUrl}
                  alt="Captured selfie"
                  className="w-full max-h-[360px] object-contain rounded-xl border border-gray-200 bg-white"
                />
                <div className="mt-3 text-xs text-gray-600">
                  {selfieCaptured?.name} • {bytesToMB(selfieCaptured?.size || 0)} MB
                </div>
              </div>
            ) : (
              <div className="text-sm text-gray-500">No selfie captured yet.</div>
            )}
          </div>
        )}

        <div className="mt-6 flex items-center justify-end">
          <button
            type="button"
            disabled={submitting}
            onClick={onSubmit}
            className="px-5 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold disabled:opacity-60"
          >
            {submitting ? "Submitting…" : "Submit KYC"}
          </button>
        </div>
      </div>

      <div className="mt-4 text-xs text-gray-500">
        By submitting, you confirm the information and documents provided are accurate.
      </div>
    </div>
  );
}
